/**
 * Subscription tier from persisted user (localStorage / API shape).
 * Paid access: any normalized tier other than `free` (case-insensitive, trimmed).
 * Supports subscriptionTier, subscription.tier, and variants like Owner-Operator / owner_operator.
 */

export function readUserFromStorage() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Normalized tier string (lowercase, trimmed). Unknown / empty → 'free'.
 * Reads nested `subscription.tier` from jsonb (object or JSON string from API).
 */
export function getSubscriptionTier(user) {
  if (!user || typeof user !== 'object') return 'free';

  let raw;
  const sub = user.subscription;
  if (sub != null) {
    let parsed = sub;
    if (typeof sub === 'string') {
      try {
        parsed = JSON.parse(sub);
      } catch {
        parsed = null;
      }
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.tier != null && parsed.tier !== '') {
      raw = parsed.tier;
    }
  }
  if (raw == null || raw === '') {
    raw = user.subscriptionTier;
  }
  if (raw == null || raw === '') return 'free';
  const tier = String(raw).toLowerCase().trim();
  if (tier === '' || tier === 'null' || tier === 'undefined') return 'free';
  return tier;
}

export function isFreeSubscription(user) {
  return getSubscriptionTier(user) === 'free';
}

/**
 * Dispatched on `window` after the persisted `user` is refreshed from the API.
 * The `storage` event only fires in other tabs, so same-tab listeners (e.g. the
 * sidebar, which mounts once and never unmounts) need this to pick up a tier change.
 */
export const SUBSCRIPTION_UPDATED_EVENT = 'sovrynn:subscription-updated';

export function notifySubscriptionUpdated() {
  try {
    window.dispatchEvent(new Event(SUBSCRIPTION_UPDATED_EVENT));
  } catch {
    /* no-op when there is no window */
  }
}

/** Ensure localStorage/API user objects include subscriptionTier aligned with subscription.tier. */
export function mergeSubscriptionTier(user) {
  if (!user || typeof user !== 'object') return user;
  return { ...user, subscriptionTier: getSubscriptionTier(user) };
}

export function hasAuthorityCopilotPlanAccess(user) {
  return !isFreeSubscription(user);
}
